import { useState, useCallback, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useReducedMotion } from 'framer-motion';
import { ChevronLeft, ChevronRight, Volume2, RotateCcw, CheckCircle, XCircle } from 'lucide-react';
import { useLanguage } from '../../../contexts/LanguageContext';
import { useSoundEffects } from '../../../hooks/cabin/useSoundEffects';

export default function FlashCard({
  phase,
  onPhaseComplete,
  'data-testid': testId = 'flash-card',
}) {
  const { t, lang } = useLanguage();
  const { play } = useSoundEffects();
  const shouldReduce = useReducedMotion();

  const cards = phase?.content?.cards || [];
  const maxXP = phase?.xp_in_phase || 0;

  const [currentIndex, setCurrentIndex] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const [knownCards, setKnownCards] = useState(new Set());
  const [unknownCards, setUnknownCards] = useState(new Set());
  const [finished, setFinished] = useState(false);
  const audioRef = useRef(null);

  const currentCard = cards[currentIndex];
  const isLast = currentIndex === cards.length - 1;
  const reviewedCount = knownCards.size + unknownCards.size;

  const frontText =
    lang === 'tr'
      ? (currentCard?.front_tr || currentCard?.front || '')
      : (currentCard?.front_en || currentCard?.front_tr || currentCard?.front || '');

  const backText =
    lang === 'tr'
      ? (currentCard?.back_tr || currentCard?.back || '')
      : (currentCard?.back_en || currentCard?.back_tr || currentCard?.back || '');

  const exampleText =
    lang === 'tr'
      ? currentCard?.example_tr || currentCard?.example
      : currentCard?.example_en || currentCard?.example_tr || currentCard?.example;

  const handleFlip = useCallback(() => {
    play('click');
    setFlipped((f) => !f);
  }, [play]);

  const handlePlayAudio = useCallback(async () => {
    const audioUrl = currentCard?.audio_url;
    if (!audioUrl) return;

    try {
      // Lazy load audio
      if (audioRef.current) {
        audioRef.current.pause();
      }
      const audio = new Audio(audioUrl);
      audioRef.current = audio;
      await audio.play();
    } catch {
      // Audio not available — silently ignore
    }
  }, [currentCard]);

  const handleKnown = useCallback(() => {
    play('correct');
    setKnownCards((prev) => new Set([...prev, currentCard.id || currentIndex]));

    if (isLast) {
      setFinished(true);
    } else {
      setCurrentIndex((i) => i + 1);
      setFlipped(false);
    }
  }, [play, currentCard, currentIndex, isLast]);

  const handleUnknown = useCallback(() => {
    play('wrong');
    setUnknownCards((prev) => new Set([...prev, currentCard.id || currentIndex]));

    if (isLast) {
      setFinished(true);
    } else {
      setCurrentIndex((i) => i + 1);
      setFlipped(false);
    }
  }, [play, currentCard, currentIndex, isLast]);

  const handlePrev = useCallback(() => {
    if (currentIndex === 0) return;
    play('click');
    setCurrentIndex((i) => i - 1);
    setFlipped(false);
  }, [currentIndex, play]);

  const handleNext = useCallback(() => {
    if (currentIndex === cards.length - 1) return;
    play('click');
    setCurrentIndex((i) => i + 1);
    setFlipped(false);
  }, [currentIndex, cards.length, play]);

  // Keyboard navigation
  useEffect(() => {
    const handler = (e) => {
      if (e.key === ' ' || e.key === 'Enter') {
        e.preventDefault();
        handleFlip();
      }
      if (e.key === 'ArrowLeft') handlePrev();
      if (e.key === 'ArrowRight') handleNext();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [handleFlip, handlePrev, handleNext]);

  const handleComplete = useCallback(() => {
    const knownCount = knownCards.size;
    const score = cards.length > 0
      ? Math.round((knownCount / cards.length) * maxXP)
      : 0;
    onPhaseComplete({
      score,
      maxScore: maxXP,
      answers: { known: knownCount, unknown: unknownCards.size, total: cards.length },
      timeSpent: 0,
    });
  }, [knownCards, unknownCards, cards.length, maxXP, onPhaseComplete]);

  useEffect(() => {
    if (finished) {
      const timer = setTimeout(handleComplete, 800);
      return () => clearTimeout(timer);
    }
  }, [finished, handleComplete]);

  if (!currentCard) return null;

  if (finished) {
    return (
      <div className="flex flex-col items-center gap-4 py-8 text-center" data-testid={`${testId}-done`}>
        <div className="text-4xl" aria-hidden="true">🎴</div>
        <p className="text-lg font-bold text-[var(--text-main)]">
          {t('exercise.flashcard_done') || 'Kart destesi tamamlandı!'}
        </p>
        <p className="text-sm text-[var(--text-muted)]">
          {t('exercise.flashcard_known') || 'Bildiğim'}: <strong className="text-emerald-600">{knownCards.size}</strong>
          {' / '}
          {t('exercise.flashcard_total') || 'Toplam'}: <strong>{cards.length}</strong>
        </p>
      </div>
    );
  }

  const flipVariants = {
    front: { rotateY: 0 },
    back: { rotateY: 180 },
  };

  return (
    <div className="flex flex-col gap-6 w-full max-w-md mx-auto" data-testid={testId}>
      {/* Progress */}
      <div className="flex items-center justify-between text-sm text-[var(--text-muted)]">
        <span data-testid={`${testId}-progress`}>
          {currentIndex + 1} / {cards.length}
        </span>
        <div className="flex items-center gap-3">
          <span className="text-emerald-600 font-semibold">
            {t('exercise.known') || 'Bildi'}: {knownCards.size}
          </span>
          <span className="text-red-500 font-semibold">
            {t('exercise.unknown') || 'Bilmedi'}: {unknownCards.size}
          </span>
        </div>
      </div>

      {/* Progress bar */}
      <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden" role="progressbar"
        aria-valuenow={reviewedCount} aria-valuemin={0} aria-valuemax={cards.length}
        aria-label={t('exercise.progress_label') || 'Kart ilerlemesi'}
      >
        <div
          className="h-full bg-[var(--brand-primary)] rounded-full transition-all duration-300"
          style={{ width: `${(reviewedCount / Math.max(cards.length, 1)) * 100}%` }}
        />
      </div>

      {/* Card */}
      <div
        className="relative h-48 cursor-pointer"
        onClick={handleFlip}
        style={{ perspective: '800px' }}
        role="button"
        aria-pressed={flipped}
        aria-label={flipped ? (t('exercise.card_back') || 'Kartın arkası') : (t('exercise.card_front') || 'Kartın ön yüzü')}
        data-testid={`${testId}-card`}
        tabIndex={0}
      >
        <motion.div
          animate={shouldReduce ? {} : (flipped ? flipVariants.back : flipVariants.front)}
          initial={false}
          transition={{ duration: 0.4, ease: 'easeInOut' }}
          style={{ transformStyle: 'preserve-3d' }}
          className="relative w-full h-full"
        >
          {/* Front face */}
          <div
            className="absolute inset-0 flex flex-col items-center justify-center gap-3 p-6 rounded-2xl border-2 border-[var(--border-light)] bg-white shadow-[0_4px_0_0_var(--border-light)]"
            style={{ backfaceVisibility: 'hidden' }}
          >
            <p className="text-2xl font-black text-[var(--text-main)] text-center" aria-live="polite">
              {frontText}
            </p>
            {currentCard.audio_url && (
              <button
                onClick={(e) => { e.stopPropagation(); handlePlayAudio(); }}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-primary)]"
                aria-label={t('exercise.play_audio') || 'Sesi oynat'}
                data-testid={`${testId}-audio`}
              >
                <Volume2 size={18} className="text-[var(--text-muted)]" aria-hidden="true" />
              </button>
            )}
            <p className="text-xs text-[var(--text-muted)]">
              {t('exercise.tap_to_flip') || 'Çevirmek için tıklayın'}
            </p>
          </div>

          {/* Back face */}
          <div
            className="absolute inset-0 flex flex-col items-center justify-center gap-3 p-6 rounded-2xl border-2 border-[var(--brand-primary)] bg-sky-50 shadow-[0_4px_0_0_#0369A1]"
            style={{ backfaceVisibility: 'hidden', transform: 'rotateY(180deg)' }}
          >
            <p className="text-xl font-black text-sky-700 text-center" aria-live="polite">
              {backText}
            </p>
            {exampleText && (
              <p className="text-sm text-[var(--text-muted)] italic text-center">
                &ldquo;{exampleText}&rdquo;
              </p>
            )}
          </div>
        </motion.div>
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between">
        <button
          onClick={handlePrev}
          disabled={currentIndex === 0}
          className="p-2 rounded-xl border-2 border-[var(--border-light)] hover:border-[var(--brand-primary)] disabled:opacity-40 disabled:cursor-not-allowed transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-primary)]"
          aria-label={t('exercise.prev_card') || 'Önceki kart'}
          data-testid={`${testId}-prev`}
        >
          <ChevronLeft size={20} aria-hidden="true" />
        </button>

        <button
          onClick={handleFlip}
          className="flex items-center gap-2 px-4 py-2 rounded-xl border-2 border-[var(--border-light)] hover:border-[var(--brand-primary)] text-sm text-[var(--text-muted)] hover:text-[var(--brand-primary)] transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-primary)]"
          data-testid={`${testId}-flip`}
        >
          <RotateCcw size={14} aria-hidden="true" />
          {t('exercise.flip') || 'Çevir'}
        </button>

        <button
          onClick={handleNext}
          disabled={currentIndex === cards.length - 1}
          className="p-2 rounded-xl border-2 border-[var(--border-light)] hover:border-[var(--brand-primary)] disabled:opacity-40 disabled:cursor-not-allowed transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-primary)]"
          aria-label={t('exercise.next_card') || 'Sonraki kart'}
          data-testid={`${testId}-next-btn`}
        >
          <ChevronRight size={20} aria-hidden="true" />
        </button>
      </div>

      {/* Known / Unknown buttons (visible only when flipped) */}
      <AnimatePresence>
        {flipped && (
          <motion.div
            key="actions"
            initial={shouldReduce ? {} : { opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.2 }}
            className="flex gap-3"
          >
            <motion.button
              whileHover={shouldReduce ? {} : { scale: 1.02 }}
              whileTap={shouldReduce ? {} : { scale: 0.98 }}
              onClick={handleUnknown}
              className="flex-1 flex items-center justify-center gap-2 py-3 border-2 border-[var(--brand-danger)] text-red-600 font-bold rounded-xl hover:bg-[var(--brand-danger)]/10 transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-danger)]"
              data-testid={`${testId}-unknown`}
            >
              <XCircle size={18} aria-hidden="true" />
              {t('exercise.unknown_btn') || 'Bilmedim'}
            </motion.button>

            <motion.button
              whileHover={shouldReduce ? {} : { scale: 1.02 }}
              whileTap={shouldReduce ? {} : { scale: 0.98 }}
              onClick={handleKnown}
              className="flex-1 flex items-center justify-center gap-2 py-3 border-2 border-[#10B981] text-emerald-600 font-bold rounded-xl hover:bg-[#10B981]/10 transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#10B981]"
              data-testid={`${testId}-known`}
            >
              <CheckCircle size={18} aria-hidden="true" />
              {t('exercise.known_btn') || 'Bildim'}
            </motion.button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
